import { z, isoZ } from 'my-zod'
import { productLineOptions } from '../../internal/product-lines'
import { enumOptionsToObject, ProgramNameEnum } from '../../internal'
import { operatorAddressFragment } from '../../utilities/address/operator-address-fragment'
import { DepartmentNumbersEnum } from './department-numbers'
import { outfitterCompetitiveRentalInventory } from './outfitter-competitive-rental-inventory'
import { CompetitiveBrandsUtilizedEnum } from './competitive-brands-utilized'

export const ExtraAppFeaturesEnum = z.enum([
  // For legacy mobile app
  'Sync Diagnostics',
  // For legacy mobile app
  'CloudLogging',
  // For legacy mobile app
  'ORVDealerRentalProgram',
  // For legacy mobile app
  'CloudTelemetry',
  // For legacy mobile app
  'CloudTelemetryForImages',
  // For legacy mobile app
  'Tour Mode',
  // For legacy web waivers
  'SignedWebWaivers',
  // For legacy mobile app
  'SyncPushNotifications',
  // For legacy mobile app
  'UITelemetry',
  // To designate an outfitter gets EHS treatment - for legacy web waivers
  'EmployeeHealthSafety',
  // For legacy mobile app
  'SelectReservations',
  // For legacy mobile app - but not used there - obsolete
  'WaiverV2',
  // For legacy mobile app - but not used there - obsolete
  'DisableMobileFormScreenshots',
  // For legacy mobile app - but not used there - obsolete
  'UnifiedApp',
  // Legacy mobile app - Turns on the option on the outfitter mobile app to use Butler api for license scanning instead of Microblink
  'ScanLicenseUsingButler',
  // Used by HQ to indicate this outfitter should see newer features.  This is different than the beta "role" that allows you to
  // see new features as a user within *any* outfitter - this switch is to turn on new features for *all* users of an outfitter.
  'HQBetaFeatures',
  // Used by HQ to indicate that this outfitter requires MFA for owner and beta roles.  Temporary measure so that we don't need to require it
  // for everyone in advance of full rollout of credit reimbursement for outfitters, which is what drives desire for MFA since banking details can be accessed.
  'HQCanaryNetworkRequest'
])
export type ExtraAppFeature = z.infer<typeof ExtraAppFeaturesEnum>
export const ExtraAppFeatures = ExtraAppFeaturesEnum.Values

export const OutfitterVisibilityStatusEnum = z.enum(['Draft', 'Live'])
export const OutfitterVisibilityStatuses = OutfitterVisibilityStatusEnum.Values

export const AdditionalLanguagesEnum = z.enum(['es-mx', 'fr-ca', 'fr', 'pl'])

export const SamsungTabletAppsEnum = z.enum(['Avenza', 'Ride Command'])

export const ReservationSystemsEnum = z
  .enum(['FareHarbor', 'Peak', 'Rezd', 'Flybook', 'MPWR', 'Other'])
  .describe('Current customer reservation system used by outfitter.')
export const ReservationSystems = ReservationSystemsEnum.Values

export const ProspectSourceAppEnum = z
  .enum([
    'Website',
    'Customer',
    'ReferralPolarisAdventuresOutfitter',
    'ReferralDealer',
    'PolarisEmployee',
    'ConferenceTradeShow',
    'Other'
  ])
  .setEnumLabels({
    Customer: 'Customer',
    PolarisEmployee: 'Polaris employee',
    ConferenceTradeShow: 'Conference/trade show',
    Other: 'Other',
    ReferralPolarisAdventuresOutfitter: 'Referral-polaris adventures outfitter',
    Website: 'Website',
    ReferralDealer: 'Referral dealer'
  })
export type ProspectSourceAppEnum = z.infer<typeof ProspectSourceAppEnum>

export const ProspectSourceEnum = z
  .enum([
    'Website',
    'Customer',
    'ReferralPolarisAdventuresOutfitter',
    'ReferralDealer',
    'PolarisEmployee',
    'ConferenceTradeShow',
    'Other',
    'ColdCall',
    'Expansion'
  ])
  .setEnumLabels({
    ColdCall: 'Cold call',
    Expansion: 'Expansion',
    Customer: 'Customer',
    PolarisEmployee: 'Polaris employee',
    ConferenceTradeShow: 'Conference/trade show',
    Other: 'Other',
    ReferralPolarisAdventuresOutfitter: 'Referral-polaris adventures outfitter',
    Website: 'Website',
    ReferralDealer: 'Referral dealer'
  })
export type ProspectSourceEnum = z.infer<typeof ProspectSourceEnum>

export const prospectStatusOptions = [
  'Contacted',
  'DealLost',
  'DealWon',
  'Negotiations',
  'New',
  'Prospecting',
  'Qualified',
  'Stuck',
  'VerbalAgreement_OutForSignature'
] as const
export const prospectStatuses = enumOptionsToObject(prospectStatusOptions)
export type ProspectStatus = (typeof prospectStatusOptions)[number]
export const prospectStatusLabels: Record<ProspectStatus, string> = {
  Contacted: 'Contacted',
  DealLost: 'Deal Lost',
  DealWon: 'Deal Won',
  Negotiations: 'Negotiations',
  New: 'New',
  Prospecting: 'Prospecting',
  Qualified: 'Qualified',
  Stuck: 'Stuck',
  VerbalAgreement_OutForSignature: 'Verbal Agreement/Out For Signature'
}

export const ProspectStatusEnum = z.enum(prospectStatusOptions).setEnumLabels(prospectStatusLabels)

export const LifecycleStatusEnum = z
  .enum(['Prospect', 'Active', 'ActiveAtRisk', 'Deactivated', 'PendingTermination'])
  .setEnumLabels({
    Prospect: 'Prospect',
    Active: 'Active',
    ActiveAtRisk: 'Active - At Risk',
    Deactivated: 'Deactivated',
    PendingTermination: 'Pending Termination'
  })
  .describe("Status of the outfitter's relationship with Polaris Adventures.")

export type LifecycleStatusEnum = z.infer<typeof LifecycleStatusEnum>
export const LifecycleStatuses = LifecycleStatusEnum.Values

export const DeactivationReasonCodeEnum = z
  .enum([
    'VoluntaryOutfitterClosing',
    'VoluntaryCompetitiveLoss',
    'VoluntaryGoingIndependent',
    'InvoluntaryFinancialPerformance',
    'InvoluntaryProgramAdherence',
    'InvoluntarySafetyLegal',
    'InvoluntaryLackOfGrowth'
  ])
  .setEnumLabels({
    VoluntaryOutfitterClosing: 'Voluntary Outfitter Closing',
    VoluntaryCompetitiveLoss: 'Voluntary Competitive Loss',
    VoluntaryGoingIndependent: 'Voluntary Going Independent',
    InvoluntaryFinancialPerformance: 'Involuntary Financial Performance',
    InvoluntaryProgramAdherence: 'Involuntary Program Adherence',
    InvoluntarySafetyLegal: 'Involuntary Safety Legal',
    InvoluntaryLackOfGrowth: 'Involuntary Lack Of Growth'
  })
  .describe('Outfitters being deactivation reason.')

export type DeactivationReasonCodeEnum = z.infer<typeof DeactivationReasonCodeEnum>

export const CompetitiveAffiliationsEnum = z
  .enum([
    'PolarisAdventures',
    'UnchartedSociety',
    'DestinationYamaha',
    'YourBoatClub',
    'FreedomBoatClub',
    'CarefreeBoatClub',
    'NauticalBoatClub',
    'EagleRider',
    'Independent'
  ])
  .setEnumLabels({
    PolarisAdventures: 'Polaris Adventures',
    UnchartedSociety: 'Uncharted Society',
    DestinationYamaha: 'Destination Yamaha',
    YourBoatClub: 'Your Boat Club',
    FreedomBoatClub: 'Freedom Boat Club',
    CarefreeBoatClub: 'Carefree Boat Club',
    NauticalBoatClub: 'Nautical Boat Club',
    EagleRider: 'Eagle Rider',
    Independent: 'Independent'
  })

export type CompetitiveAffiliationsEnum = z.infer<typeof CompetitiveAffiliationsEnum>

export const BusinessTypeEnum = z
  .enum([
    'ExistingRentalNonPowersports',
    'ExistingPowersportsRental',
    'Dealer',
    'NewBusinessStartup',
    'ResortHotel',
    'Entrepreneur',
    'RanchHuntingLodge',
    'Other'
  ])
  .setEnumLabels({
    ExistingRentalNonPowersports: 'Existing Rental Non-Powersports',
    ExistingPowersportsRental: 'Existing Powersports Rental',
    Dealer: 'Dealer',
    NewBusinessStartup: 'New Business Startup',
    ResortHotel: 'Resort / Hotel',
    Entrepreneur: 'Entrepreneur',
    RanchHuntingLodge: 'Ranch/Hunting Lodge',
    Other: 'Other'
  })

export type BusinessTypeEnum = z.infer<typeof BusinessTypeEnum>

export const TypeOfExperienceEnum = z.enum(['Guided', 'Unguided', 'Both'])
export type TypeOfExperienceEnum = z.infer<typeof TypeOfExperienceEnum>

export const ExpectedImpactEnum = z.enum([
  'Marketing Support',
  'Access to Vehicles',
  'Becoming a Part of the Network',
  'Premium Process and Technology'
])
export type ExpectedImpactEnum = z.infer<typeof ExpectedImpactEnum>

export const vehicleTypeOptions = ['Slingshot', 'Off-Road Vehicle', 'Snowmobile', 'Boat', 'Motorcycles'] as const
export const VehicleTypeEnum = z.enum(vehicleTypeOptions)
export type VehicleTypeEnum = z.infer<typeof VehicleTypeEnum>

export const masterServiceDealerEnrollmentStatusEnum = z
  .enum(['NotEnrolled', 'Requested', 'PartiallyCertified', 'Enrolled', 'DealerCertified'])
  .setEnumLabels({
    NotEnrolled: 'Outfitter Not Certified',
    Requested: 'Not Certified',
    Enrolled: 'Outfitter Certified',
    PartiallyCertified: 'Outfitter Partially Certified',
    DealerCertified: 'Dealer Certified'
  })

export const masterServiceDealerEnrollmentStatusLabels: Record<
  z.infer<typeof masterServiceDealerEnrollmentStatusEnum>,
  string
> = {
  NotEnrolled: 'Outfitter Not Certified',
  Requested: 'Not Certified',
  Enrolled: 'Outfitter Certified',
  PartiallyCertified: 'Outfitter Partially Certified',
  DealerCertified: 'Dealer Certified'
}

export type masterServiceDealerEnrollmentStatusEnum = z.infer<typeof masterServiceDealerEnrollmentStatusEnum>

export const dealConfidenceOptions = ['0', '20', '40', '60', '80', '100'] as const
export const dealConfidence = enumOptionsToObject(dealConfidenceOptions)
export type DealConfidence = (typeof dealConfidenceOptions)[number]
export const dealConfidenceLabels: Record<DealConfidence, string> = {
  '0': '0%',
  '20': '20%',
  '40': '40%',
  '60': '60%',
  '80': '80%',
  '100': '100%'
}

export const DealConfidenceEnum = z.enum(dealConfidenceOptions).setEnumLabels(dealConfidenceLabels)

export const operatorsModel = z
  .object({
    id: z.number(),
    shortId: z.string(),
    businessName: z.string().min(1).max(100).describe('outfitter public-facing name'),
    dealerNumber: z.string().nullable(),
    emails: z.string().array().nullable().describe('emails of outfitter to login to their app'),
    primaryContactId: z.string().uuid().nullable(),
    businessPhone: z.string().nullable().describe('customer-facing phone'),
    businessEmail: z.string().nullable(),
    activeProgramAgreementId: z.string().uuid().nullable(),
    outfitterLegalEntity: z.string().nullable(),
    customerDestinationAddressId: z.string().uuid().nullable(),
    shippingAddressId: z.string().uuid().nullable(),
    billingAddressId: z.string().uuid().nullable(),
    legalAddressId: z.string().uuid().nullable(),
    vehicleDeliveryAddressId: z.string().uuid().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    region: z.string().nullish(),
    urlSlug: z
      .string()
      .nullable()
      .describe(
        'The portion of the URL used at the end on the marketing site, e.g. https://adventures.polaris.com/our-outfitters/silverton-rock-pirates/.   Slugs must be unique in the system.  You may wish to set a slug value manually to match its previous value from the old marketing site.'
      ),
    externalIntegrationSlug: z.string().nullable(),
    extraAppFeatures: ExtraAppFeaturesEnum.array().nullable(),
    expectedMobileAppVersion: z.string().nullable(),
    accountSpecialistId: z.number().nullable(),
    groupId: z.number(),
    preferredDealerId: z.number().nullable(),
    notes: z.string().nullable(),
    vehicleRegistrationNotes: z.string().nullable(),
    programName: ProgramNameEnum,
    eliteDesignationDate: isoZ.instant().nullable(),
    productLines: z.enum(productLineOptions).array().nullable(),
    additionalLanguages: AdditionalLanguagesEnum.array().nullable(),
    samsungTabletApp: SamsungTabletAppsEnum.nullable(),
    website: z.string().nullish(),
    reservationSystem: ReservationSystemsEnum.nullable(),
    reservationSystemOther: z.string().nullable(),
    riskSpecialistId: z.number().nullable(),
    fleetManagerId: z.number().nullable(),
    serviceOpsId: z.number().nullable(),
    businessDevelopmentRepId: z.number().nullable(),
    businessDevelopmentSupport: z.string().nullable(),
    deviceFriendlyNamePrefix: z.string().nullish(),
    lastModifiedDate: isoZ.instant().nullable(),
    deletedDate: isoZ.instant().nullable(),
    createdDate: isoZ.instant().nullable(),
    addresses: operatorAddressFragment.array(),
    visibilityStatus: OutfitterVisibilityStatusEnum.nullable().describe(
      'Used to determine whether this displays on the National Website.'
    ),
    overview: z.string().max(3000).nullable(),
    hoursOfOperation: z.string().max(1000).nullable(),
    landingSlug: z.string().nullable().describe('Sets URL for web-waiver (i.e. adrenalin-atv).'),
    seoMetaDescription: z.string().nullable(),
    isPremierCertified: z.boolean().nullable(),
    isSelectOutfitter: z.boolean().nullable(),
    indirectVendor: z.string().nullable().describe('Account Number for outfitter billing and reimbursements.'),
    prospectSource: ProspectSourceEnum.nullable(),
    prospectStatus: ProspectStatusEnum.nullable(),
    forecastedCloseDate: isoZ.date().nullable(),
    dealConfidence: DealConfidenceEnum.nullable(),
    lifecycleStatus: LifecycleStatusEnum,
    outfitterSignedDate: isoZ.date().nullable(),
    deactivationReasonCode: DeactivationReasonCodeEnum.nullable(),
    lossDate: isoZ.date().nullable(),
    probationDate: isoZ.instant().nullable(),
    deactivationDescription: z.string().max(512).nullable().setLabel('Deactivation Reason Description'),
    facebookLink: z.string().max(255).nullable(),
    instagramLink: z.string().max(255).nullable(),
    youtubeLink: z.string().max(255).nullable(),
    tripAdvisorLink: z.string().max(255).nullable(),
    yelpLink: z.string().max(255).nullable(),
    reviewLinks: z.string().url().array().describe('Preferred Review Platform is first in order'),
    metaPixelId: z.string().max(32).nullable(),
    heroImageUrl: z.string().nullish(),
    heroImageUrlAlt: z.string().nullish(),
    logoImage: z.string().nullish(),
    logoImageAlt: z.string().nullish(),
    cardImage: z.string().nullable(),
    cardImageAlt: z.string().nullable(),
    landingHeroImage: z.string().nullish(),
    landingHeroImageAlt: z.string().nullish(),
    galleryImages: z.string().array().max(10).nullish(),
    galleryImagesAlts: z.string().array().max(10).nullish(),
    optInEmployeeDiscount: z.boolean().nullable().setLabel('Opt-In Polaris Employee Discount Program'),
    departmentNumber: DepartmentNumbersEnum.nullable()
      .setLabel('Department Number')
      .describe('For Internal Polaris Accounts that own units and are managed in Arrival CRM'),
    reservationDepositAmount: z.number(),
    reservationDepositUnit: z.enum(['percentage', 'fixed', 'full']),
    cancellationPolicy: z.string().nullable(),
    competitiveAffiliations: CompetitiveAffiliationsEnum.array().nullable(),
    competitiveBrandsUtilized: CompetitiveBrandsUtilizedEnum.array().nullable(),
    competitiveRentalInventory: outfitterCompetitiveRentalInventory.omit({ outfitterId: true }).array(),
    marketPresenceId: z.number().nullable(),
    businessType: BusinessTypeEnum.nullable().optional(),
    typeOfExperience: TypeOfExperienceEnum.nullable().optional(),
    yearRoundSeason: z.boolean().nullable().optional(),
    masterServiceDealerEnrollmentStatus: masterServiceDealerEnrollmentStatusEnum.nullable().optional()
  })
  .setCFV({
    prospectStatusRequired(input) {
      if (!input.prospectStatus && input.lifecycleStatus === 'Prospect') {
        return {
          message: 'Required',
          path: ['prospectStatus']
        }
      }
    },
    deactivationReasonCodeRequired(input) {
      if (
        !input.deactivationReasonCode &&
        (input.lifecycleStatus === LifecycleStatusEnum.Values.ActiveAtRisk ||
          input.lifecycleStatus === LifecycleStatusEnum.Values.Deactivated)
      ) {
        return {
          message: 'Required',
          path: ['deactivationReasonCode']
        }
      }
    },
    deactivationDescriptionRequired(input) {
      if (!input.deactivationDescription && input.lifecycleStatus === LifecycleStatusEnum.Values.Deactivated) {
        return {
          message: 'Required',
          path: ['deactivationDescription']
        }
      }

      if (
        !input.deactivationDescription &&
        input.lifecycleStatus === LifecycleStatusEnum.Values.PendingTermination &&
        input.deactivationReasonCode
      ) {
        return {
          message: 'Required',
          path: ['deactivationDescription']
        }
      }
    },
    lossDateRequired(input) {
      if (!input.lossDate && input.lifecycleStatus === LifecycleStatusEnum.Values.Deactivated) {
        return {
          message: 'Required',
          path: ['lossDate']
        }
      }
    },
    socialMediaLink(input) {
      const errors = [
        { key: 'facebookLink', value: input.facebookLink },
        { key: 'instagramLink', value: input.instagramLink },
        { key: 'youtubeLink', value: input.youtubeLink },
        { key: 'tripAdvisorLink', value: input.tripAdvisorLink },
        { key: 'yelpLink', value: input.yelpLink }
      ].filter((item) => !!item.value && !item.value.startsWith('https://'))

      if (errors.length !== 0) {
        return {
          path: [errors[0].key],
          message: 'Link must start with https://'
        }
      }
    },
    forecastedCloseDateRequired(input) {
      if (
        !input.forecastedCloseDate &&
        input.prospectStatus &&
        input.prospectStatus !== prospectStatuses.New &&
        input.prospectStatus !== prospectStatuses.Prospecting &&
        input.prospectStatus !== prospectStatuses.Contacted &&
        input.prospectStatus !== prospectStatuses.DealLost
      ) {
        return {
          message: 'Required',
          path: ['forecastedCloseDate']
        }
      }
    },
    dealConfidenceRequired(input) {
      if (!input.dealConfidence && input.prospectStatus && input.prospectStatus !== prospectStatuses.New) {
        return {
          message: 'Required',
          path: ['dealConfidence']
        }
      }
    }
  })

export type operatorsModel = z.infer<typeof operatorsModel>
